import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import Reveal from "@/components/Reveal";
import { SERVICES, QUOTE_SERVICES, IMAGES } from "@/data/content";

const QUOTE_CARD_IMAGES = {
  "ceramic-coating": { src: IMAGES.ceramic, alt: "Satin grey Mercedes-AMG G63 after ceramic coating" },
  "ppf-vinyl-wraps": { src: IMAGES.ppf, alt: "Grey Ferrari in the studio, protection film work" },
  "window-tinting": { src: IMAGES.tint, alt: "Gloss black Audi S3 with dark tinted glass" },
  "chrome-delete": { src: IMAGES.blackout, alt: "Blacked-out BMW M4, front-on" },
  "caliper-resprays": { src: IMAGES.ferrariWheel, alt: "Audi RS wheel with red brake caliper" },
};

const Services = () => {
  usePageMeta(
    "Services — Luxpress Detailing, South Melbourne",
    "Detailing packages from $99, ceramic coating, paint protection film, window tinting, chrome delete and caliper resprays in South Melbourne."
  );
  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-36 sm:px-8 sm:pt-44" data-testid="services-page">
      <Reveal>
        <p className="kicker">Services</p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl">
          Everything we do<br />to paint, glass<br />&amp; <span className="text-mint">trim.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-chrome">
          Fixed-price detailing packages below. Protection work — ceramic, film, tint — is quoted per vehicle,
          because no two cars arrive in the same condition.
        </p>
      </Reveal>

      <div className="mt-16">
        <Reveal><p className="spec-label mb-4">Fixed-price packages</p></Reveal>
        <div className="border-t border-teal-line">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.04}>
              <Link
                to={`/contact?service=${s.id}`}
                data-testid={`services-row-${s.id}`}
                className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-4 border-b border-teal-line py-6 transition-colors duration-300 hover:bg-panel sm:grid-cols-[3rem_1fr_auto_auto_auto] sm:gap-8 sm:px-4"
              >
                <span className="font-mono text-xs text-chrome/60">0{i + 1}</span>
                <span>
                  <span className="block font-display text-lg font-bold uppercase tracking-tight transition-colors duration-300 group-hover:text-mint sm:text-2xl">{s.name}</span>
                  <span className="mt-1 block text-sm text-chrome">{s.covers}</span>
                </span>
                <span className="hidden font-mono text-xs uppercase tracking-[0.18em] text-chrome sm:block">{s.duration}</span>
                <span className="font-mono text-sm text-mint sm:text-base">from ${s.price}</span>
                <ArrowUpRight className="hidden h-5 w-5 text-chrome transition-colors duration-300 group-hover:text-mint sm:block" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-20">
        <Reveal><p className="spec-label mb-4">Quoted per vehicle</p></Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {QUOTE_SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06} className="flex flex-col overflow-hidden border border-teal-line bg-panel transition-colors duration-300 hover:border-mint/40">
              <img src={QUOTE_CARD_IMAGES[s.id].src} alt={QUOTE_CARD_IMAGES[s.id].alt} loading="lazy" className="aspect-[16/9] w-full object-cover" />
              <div className="flex flex-1 flex-col p-8">
                <span className="font-mono text-xs text-mint">0{i + 6}</span>
                <h2 className="mt-4 font-display text-xl font-bold uppercase tracking-tight">{s.name}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-chrome">{s.blurb}</p>
                <Link
                  to={s.path || `/contact?service=${s.id}`}
                  data-testid={`services-quote-${s.id}`}
                  className="group mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mint"
                >
                  {s.path ? "View service" : "Request a quote"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Services;
